import { gql, type TypedDocumentNode } from "@apollo/client";
import { PAGE_INFO_FRAGMENT } from "@/hooks/usePagination";
import type {
  BackofficeCommitteeAssignmentsQueryResponse,
  BackofficeCommitteeAssignmentsQueryVariables,
  BackofficeCommitteeQueryResponse,
  BackofficeCommitteeQueryVariables,
  BackofficeCommitteesQueryResponse,
  BackofficeCommitteesQueryVariables,
  CommitteeAssignmentTitlesQueryResponse
} from "./types";

export type NationalSectionQueryResponse = {
  crmSections: { nodes: { id: string; name: string; legacyId: string }[] };
};

export type NationalSectionQueryVariables = {
  input: { search: string };
};

export const NATIONAL_SECTION_QUERY: TypedDocumentNode<NationalSectionQueryResponse, NationalSectionQueryVariables> =
  gql`
    query NationalSection($input: BackofficeCRMSectionsInput!) {
      crmSections(input: $input) {
        nodes {
          id
          name
          legacyId
        }
      }
    }
  `;

export const BACKOFFICE_COMMITTEES_QUERY: TypedDocumentNode<
  BackofficeCommitteesQueryResponse,
  BackofficeCommitteesQueryVariables
> = gql`
  query BackofficeCommittees(
    $first: Int
    $last: Int
    $before: String
    $after: String
    $input: BackofficeCommitteesInput
    $orderBy: BackofficeCommitteeOrderByInput
  ) {
    backofficeCommittees(first: $first, last: $last, before: $before, after: $after, input: $input, orderBy: $orderBy) {
      nodes {
        id
        name
        status
        core
        activeParticipantCount
        inactiveParticipantCount
        totalParticipantCount
      }
      ...PageInfo
    }
  }
  ${PAGE_INFO_FRAGMENT}
`;

export const BACKOFFICE_COMMITTEE_QUERY: TypedDocumentNode<
  BackofficeCommitteeQueryResponse,
  BackofficeCommitteeQueryVariables
> = gql`
  query BackofficeCommittee($id: ID!) {
    backofficeCommittee(id: $id) {
      id
      name
      status
      core
      activeParticipantCount
      inactiveParticipantCount
      totalParticipantCount
      section {
        id
      }
    }
  }
`;

export const COMMITTEE_ASSIGNMENT_TITLES_QUERY: TypedDocumentNode<
  CommitteeAssignmentTitlesQueryResponse,
  Record<string, never>
> = gql`
  query CommitteeAssignmentTitles {
    committeeAssignmentTitles {
      id
      title
    }
  }
`;

export const BACKOFFICE_COMMITTEE_ASSIGNMENTS_QUERY: TypedDocumentNode<
  BackofficeCommitteeAssignmentsQueryResponse,
  BackofficeCommitteeAssignmentsQueryVariables
> = gql`
  query BackofficeCommitteeAssignments(
    $id: ID!
    $first: Int
    $last: Int
    $before: String
    $after: String
    $status: BackofficeCommitteeAssignmentStatus
    $orderBy: BackofficeCommitteeAssignmentOrderByInput
  ) {
    backofficeCommittee(id: $id) {
      id
      assignments(first: $first, last: $last, before: $before, after: $after, status: $status, orderBy: $orderBy) {
        nodes {
          id
          startDate
          endDate
          status
          title {
            id
            title
          }
          person {
            id
            pgaId
            displayName
            profilePhoto
          }
        }
        totalCount
        pageInfo {
          hasNextPage
          hasPreviousPage
          startCursor
          endCursor
        }
      }
    }
  }
`;

export const BACKOFFICE_COMMITTEE_PARTICIPANTS_QUERY: TypedDocumentNode<
  BackofficeCommitteeAssignmentsQueryResponse,
  BackofficeCommitteeAssignmentsQueryVariables
> = gql`
  query BackofficeCommitteeParticipants(
    $id: ID!
    $first: Int
    $last: Int
    $before: String
    $after: String
    $status: BackofficeCommitteeAssignmentStatus
    $search: String
    $orderBy: BackofficeCommitteeAssignmentOrderByInput
  ) {
    backofficeCommittee(id: $id) {
      id
      assignments(
        first: $first
        last: $last
        before: $before
        after: $after
        status: $status
        search: $search
        orderBy: $orderBy
      ) {
        nodes {
          id
          startDate
          endDate
          status
          title {
            id
            title
          }
          person {
            id
            pgaId
            displayName
            profilePhoto
          }
        }
        totalCount
        pageInfo {
          hasNextPage
          hasPreviousPage
          startCursor
          endCursor
        }
      }
    }
  }
`;
